// ./src/components/generic/ThemeSelector/ThemeSelector.tsx
import React, { useMemo, useEffect } from "react";
import { Select, MultiSelect, MultiSelectProps } from "@mantine/core";
import { useThemes } from "@/hooks/useThemes";
import { LoadingIndicator } from "@/components/generic";
import { Theme } from "@/utils/api";
import { useApp } from "@/contexts";

export interface ThemeSelectorProps {
  /** Selected theme ID(s) - string for single select, string[] for multiple */
  value: string | string[] | null | undefined;

  /** Callback when theme selection changes */
  onChange?: (
    themeId: string | string[] | null,
    theme?: Theme | Theme[]
  ) => void;

  /** Whether to allow multiple selection */
  multiple?: boolean;

  /** Placeholder text */
  placeholder?: string;

  /** Whether to include inactive themes */
  includeInactive?: boolean;

  /** Custom filter function for themes */
  themeFilter?: (theme: Theme) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text */
  emptyText?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Additional props passed to Select/MultiSelect */
  [key: string]: any;
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({
  value,
  onChange,
  multiple = false,
  placeholder = "Select a theme",
  includeInactive = false,
  themeFilter,
  required = false,
  loadingPlaceholder = "Loading themes...",
  emptyText = "No themes available",
  error,
  disabled,
  size = "md",
  ...selectProps
}) => {
  // Use themes hook
  const { isAuthenticated } = useApp();
  const { themes, loading, isEmpty, lastError } = useThemes({
    autoLoad: true,
    isAuthenticated,
  });

  // Filter and prepare theme options
  const themeOptions = useMemo(() => {
    let filteredThemes = themes;

    // Apply status filter (status 100 = active, 0 = disabled)
    if (!includeInactive) {
      filteredThemes = filteredThemes.filter(
        (theme) => theme.status === 100 // Only show active themes
      );
    }

    // Apply custom filter if provided
    if (themeFilter) {
      filteredThemes = filteredThemes.filter(themeFilter);
    }

    // Sort by name for better UX
    filteredThemes.sort((a, b) => {
      const nameA = a.name || "Unnamed Theme";
      const nameB = b.name || "Unnamed Theme";
      return nameA.localeCompare(nameB);
    });

    // Convert to select options
    return filteredThemes.map((theme) => ({
      value: theme.id || "",
      label: theme.name || "Unnamed Theme",
      disabled: theme.status === 0, // Disabled themes have status 0
    }));
  }, [themes, includeInactive, themeFilter]);

  // Create a map for quick theme lookup by ID
  const themeMap = useMemo(() => {
    const map = new Map();
    themes.forEach((theme) => {
      if (theme.id) {
        map.set(theme.id, theme);
      }
    });
    return map;
  }, [themes]);

  // Auto-select first theme if required and no value provided
  useEffect(() => {
    if (required && themeOptions.length > 0 && !loading && onChange) {
      // For single select mode
      if (!multiple && !value) {
        const firstTheme = themeOptions[0];
        if (firstTheme && !firstTheme.disabled) {
          const themeObject = themeMap.get(firstTheme.value);
          onChange(firstTheme.value, themeObject);
        }
      }
      // For multiple select mode
      else if (
        multiple &&
        (!value || (Array.isArray(value) && value.length === 0))
      ) {
        const firstTheme = themeOptions[0];
        if (firstTheme && !firstTheme.disabled) {
          const themeObject = themeMap.get(firstTheme.value);
          onChange([firstTheme.value], [themeObject]);
        }
      }
    }
  }, [required, value, themeOptions, loading, onChange, themeMap, multiple]);

  // Handle onChange with theme object lookup
  const handleChange = (selectedValue: string | string[] | null) => {
    if (!multiple) {
      // Single select mode
      const selectedThemeId = selectedValue as string | null;
      if (selectedThemeId) {
        const selectedTheme = themeMap.get(selectedThemeId);
        onChange?.(selectedThemeId, selectedTheme);
      } else {
        // When clearing selection, pass null for both ID and theme
        onChange?.(null, undefined);
      }
    } else {
      // Multiple select mode
      const selectedThemeIds = selectedValue as string[] | null;
      if (selectedThemeIds && selectedThemeIds.length > 0) {
        const selectedThemes = selectedThemeIds
          .map((id) => themeMap.get(id))
          .filter(Boolean); // Remove any undefined values
        onChange?.(selectedThemeIds, selectedThemes);
      } else {
        // When clearing selection, pass empty arrays
        onChange?.([], []);
      }
    }
  };

  // Determine the current state and configure select props accordingly
  const getSelectState = () => {
    // Loading state
    if (loading && themes.length === 0) {
      return {
        data: [],
        placeholder: loadingPlaceholder,
        disabled: true,
        rightSection: <LoadingIndicator size={size} color="gray" />,
      };
    }

    // Error state
    if (lastError && !loading) {
      return {
        data: [],
        placeholder: "Error loading themes",
        disabled: true,
        error: error || "Failed to load themes",
      };
    }

    // Empty state
    if (isEmpty && !loading) {
      return {
        data: [],
        placeholder: emptyText,
        disabled: true,
      };
    }

    // Normal state with data
    return {
      data: themeOptions,
      placeholder,
      disabled: disabled || loading,
      rightSection:
        loading && themes.length > 0 ? (
          <LoadingIndicator size={size} color="gray" />
        ) : (
          selectProps.rightSection
        ),
    };
  };

  const selectState = getSelectState();

  // Normalize value for the select component
  const normalizedValue = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value ?? null;

  const renderMultiSelectOption: MultiSelectProps["renderOption"] = ({
    option,
  }) => {
    return option.label;
  };

  // Render the appropriate select component
  if (multiple) {
    return (
      <MultiSelect
        {...selectProps}
        size={size}
        data={selectState.data}
        renderOption={renderMultiSelectOption}
        value={normalizedValue as string[]}
        onChange={handleChange}
        placeholder={selectState.placeholder}
        clearable={!required}
        searchable
        required={required}
        disabled={selectState.disabled}
        error={selectState.error || error}
        rightSection={selectState.rightSection}
      />
    );
  }

  // Render single select
  return (
    <Select
      {...selectProps}
      size={size}
      data={selectState.data}
      value={normalizedValue as string | null}
      onChange={handleChange}
      placeholder={selectState.placeholder}
      clearable={!required}
      searchable
      required={required}
      disabled={selectState.disabled}
      error={selectState.error || error}
      rightSection={selectState.rightSection}
    />
  );
};
